import React, { useState, useEffect } from 'react';
import { func, object, number, bool, string, shape } from 'prop-types';
import { debounce } from 'debounce';
import * as styles from './styles/styles.module.scss';
import OutlinedTextField from '../../forms/textFields/OutlinedTextField';
import FormButtons from '../../MapCreator/FormButtons';
import Toggle from '../../shared/Toggle';
import { CANCEL_WAYPOINT_FORM, ADD_PERSISTED_WAYPOINT, UPDATE_WAYPOINT_EDIT_PARAMS } from '../../../hooks/useWaypoints';
import { createWaypoint, updateWaypoint, addWaypointToTrail } from '../../../utils/requests/at_map_requests';
import { formatWaypointForMap } from '../../../utils/map_creator_util';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';

const WaypointForm = ({ id, dispatchWaypoints, messagingChannel, mapId, trailId, isAdmin, createMapWithWaypoint, dispatchFormState, ...props }) => {
  const [formParams, updateFormParams] = useState({
    description: props.description,
    location: props.location,
    name: props.name,
    showTitle: !trailId && props.waypointDisplayProperty.showTitle
  });
  const [submitting, toggleSubmitting] = useState(false);
  const [serverError, updateServerError] = useState('');

  const updateTopLevelForm = debounce(() => {
    // this updates the form data in the panel, but we debounce here to limit re-renders
    const payload = { ...formParams, id };
    dispatchWaypoints({ payload, type: UPDATE_WAYPOINT_EDIT_PARAMS });
  }, 1000);

  useEffect(() => {
    if (!dispatchFormState) {
      return;
    }

    updateTopLevelForm();
  }, [formParams]);

  const handleSuccess = waypoint => {
    dispatchWaypoints({ payload: waypoint, type: ADD_PERSISTED_WAYPOINT });
  };

  const handleError = err => {
    toggleSubmitting(false);
    updateServerError(StringConstantsMixin.strings.MAP_CREATOR.WAYPOINT_ERROR);
  };

  const persistNewWaypoint = () => {
    const waypoint = !mapId || trailId ? formatWaypointForMap(formParams) : formParams;

    if (trailId) {
      addWaypointToTrail(trailId, waypoint)
        .then(handleSuccess)
        .catch(handleError);

      return;
    }

    if (!mapId && createMapWithWaypoint) {
      createMapWithWaypoint(waypoint);
      return;
    }

    createWaypoint(mapId, waypoint)
      .then(handleSuccess)
      .catch(handleError);
  };

  const handleSubmit = e => {
    e.preventDefault();

    if (id) {
      updateWaypoint(mapId, { ...formParams, id })
        .then(handleSuccess)
        .catch(handleError);
    } else {
      persistNewWaypoint();
    }
  };

  const handleWaypointDragged = data => {
    const {
      location: { latitude, longitude }
    } = data;

    const location = {
      latitude: latitude.toFixed(5),
      longitude: longitude.toFixed(5)
    };

    updateFormParams(prevState => ({ ...prevState, location }));
  };

  useEffect(() => {
    if (!messagingChannel) {
      return;
    }

    messagingChannel.subscribe('waypoint.dragged.with_location', handleWaypointDragged);
  }, []);

  const handleChange = e => {
    const formName = e.currentTarget.name;
    const { value } = e.currentTarget;

    updateFormParams(prevState => ({ ...prevState, [formName]: value }));
  };

  const handleToggle = () => {
    const { showTitle } = formParams;
    updateFormParams(prevState => ({ ...prevState, showTitle: !showTitle }));
  };

  const handleLocationChange = e => {
    const { value } = e.currentTarget;
    const fieldName = e.currentTarget.name;
    const location = { ...formParams.location, [fieldName]: value };

    updateFormParams({ ...formParams, location });
  };

  const handleCancel = () => {
    dispatchWaypoints({ type: CANCEL_WAYPOINT_FORM });
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.textFieldContainer}>
        <OutlinedTextField name="name" value={formParams.name} label={StringConstantsMixin.strings.TITLE} changeHandler={handleChange} />
      </div>
      <div className={styles.textFieldContainer}>
        <OutlinedTextField
          name="description"
          value={formParams.description}
          label={StringConstantsMixin.strings.TAB_NAMES.DESCRIPTION}
          changeHandler={handleChange}
          multiline
          verticalResize
          rows={3}
        />
      </div>
      <div className={styles.lngLat}>
        <OutlinedTextField
          name="latitude"
          value={formParams.location.latitude}
          label={StringConstantsMixin.strings.LATITUDE}
          changeHandler={handleLocationChange}
        />
        <OutlinedTextField
          name="longitude"
          value={formParams.location.longitude}
          label={StringConstantsMixin.strings.LONGITUDE}
          changeHandler={handleLocationChange}
        />
      </div>
      {!trailId && (
        <Toggle
          className={styles.formToggle}
          inputId="showTitle"
          checked={formParams.showTitle}
          onChange={handleToggle}
          arial-label={StringConstantsMixin.strings.TRACK_DETAIL.SHOW_TITLE_ON_MAP}
          label={StringConstantsMixin.strings.TRACK_DETAIL.SHOW_TITLE_ON_MAP}
        />
      )}
      {serverError && <div className={styles.errorMessage}>{serverError}</div>}
      <div className={styles.buttonAndLinks}>
        <FormButtons onCancel={handleCancel} disabled={submitting} />
        {isAdmin && id && (
          <a
            className={styles.adminButton}
            href={`/edit-translations?table=at_map_markers&column_name=name&id=${id}`}
            rel="noreferrer"
            target="_blank"
          >
            Edit Translations
          </a>
        )}
      </div>
    </form>
  );
};

WaypointForm.propTypes = {
  description: string,
  createMapWithWaypoint: func,
  dispatchWaypoints: func,
  dispatchFormState: bool,
  id: number,
  isAdmin: bool,
  location: shape({
    latitude: number,
    longitude: number
  }),
  mapId: number,
  messagingChannel: object,
  name: string,
  trailId: number,
  waypointDisplayProperty: shape({
    showTitle: bool
  })
};

WaypointForm.defaultProps = {
  createMapWithWaypoint: null,
  description: '',
  dispatchFormState: false,
  dispatchWaypoints: () => {},
  id: null,
  isAdmin: false,
  location: {
    latitude: '',
    longitude: ''
  },
  mapId: null,
  messagingChannel: null,
  name: '',
  trailId: null,
  waypointDisplayProperty: {
    showTitle: true
  }
};

export default WaypointForm;
